import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const SCHEMA_PERICIAS = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias#service`,
      name: 'Engenharia de Avaliações e Perícias Técnicas',
      description: 'Perícias judiciais e extrajudiciais, assistência técnica, avaliações de imóveis e patrimônio, pareceres técnicos e laudos especializados em Sorocaba e Campinas.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Engeporto Engenharia e Serviços Ltda.',
        url: import.meta.env.VITE_SITE_URL,
      },
      areaServed: ['Sorocaba', 'Campinas', 'Porto Feliz'],
      serviceType: 'Perícias e Avaliações Técnicas de Engenharia',
      url: `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: import.meta.env.VITE_SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Serviços', item: `${import.meta.env.VITE_SITE_URL}/servicos` },
        { '@type': 'ListItem', position: 3, name: 'Avaliações e Perícias', item: `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias` },
      ],
    },
  ],
});

const AvaliacoesPericiasPage = () => {
  useEffect(() => {
    document.title = 'Avaliações e Perícias Técnicas Sorocaba Campinas | Laudos — Engeporto';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Perícias técnicas judiciais e extrajudiciais, avaliações de imóveis, assistência técnica e laudos especializados nas Regiões Metropolitanas de Sorocaba e Campinas. Engeporto Engenharia.');
    setMeta('keywords', 'perícia técnica Sorocaba, avaliação imóvel Campinas, laudo pericial, assistência técnica judicial, avaliação patrimonial');
    setMeta('og:title', 'Avaliações e Perícias Técnicas | Laudos — Engeporto Sorocaba Campinas', true);
    setMeta('og:description', 'Perícias judiciais, avaliações de imóveis e laudos especializados em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/servicos/avaliacoes-pericias`);
    return () => { canonical?.remove(); };
  }, []);

  const servicos = [
    {
      icon: 'ri-scales-3-line',
      title: 'Perícias Técnicas Judiciais',
      description: 'Atuação como perito judicial em processos que envolvem questões técnicas de engenharia civil e segurança do trabalho, com elaboração de laudos periciais imparciais e fundamentados tecnicamente.',
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Perícias Técnicas Extrajudiciais',
      description: 'Avaliações técnicas para resolução de conflitos fora do âmbito judicial, incluindo arbitragens, mediações e acordos entre partes.',
    },
    {
      icon: 'ri-user-settings-line',
      title: 'Assistência Técnica em Processos Judiciais',
      description: 'Acompanhamento de processos judiciais como assistente técnico, prestando todo suporte necessário, formulando quesitos de forma técnica e estratégica, análise crítica de laudos periciais e elaboração de pareceres técnicos.',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Avaliações Técnicas de Imóveis',
      description: 'Avaliação de imóveis urbanos e rurais conforme normas da ABNT NBR 14653, determinação de valor de mercado, valor de locação e depreciação. Com utilização de softwares profissionais.',
    },
    {
      icon: 'ri-building-line',
      title: 'Avaliações de Edificações',
      description: 'Análise técnica de edificações, avaliação de estado de conservação, identificação de patologias construtivas e estimativa de custos de reparos.',
    },
    {
      icon: 'ri-article-line',
      title: 'Pareceres Técnicos',
      description: 'Emissão de pareceres técnicos fundamentados sobre questões específicas de engenharia, análise de projetos e avaliação de conformidade técnica.',
    },
    {
      icon: 'ri-error-warning-line',
      title: 'Diagnóstico de Não Conformidades',
      description: 'Identificação e documentação de não conformidades técnicas em edificações, instalações e sistemas, com recomendações de adequação.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Avaliações Patrimoniais',
      description: 'Avaliação de patrimônio imobiliário para fins contábeis, fiscais, securitários e de garantia bancária, conforme normas técnicas vigentes.',
    },
  ];

  const areas = [
    'Engenharia Civil',
    'Engenharia de Segurança',
    'Instalações Prediais',
    'Patologias Construtivas',
    'Avaliações Imobiliárias',
    'Perícias Trabalhistas',
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_PERICIAS }}
      />
      <Navbar />
      
      <section className="pt-36 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-4 md:mb-8 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar para Serviços
            </Link>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-bold mb-6">
                  <i className="ri-file-list-3-line text-lg"></i>
                  PILAR 3
                </div>
                
                <h1 className="font-display font-bold text-2xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 leading-tight">
                  Engenharia de Avaliações e Perícias
                </h1>
                
                <p className="text-sm md:text-xl text-gray-300 leading-relaxed">
                  Perícias técnicas judiciais e extrajudiciais, avaliações de insalubridade e periculosidade, patrimoniais e laudos especializados com rigor técnico e imparcialidade
                </p>
              </div>

              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/db31eb13cfea0e4ab562368e4211d74d.jpg"
                  alt="Avaliações e Perícias"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Serviços Especializados
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              Atuação técnica especializada em perícias e avaliações com imparcialidade, rigor técnico e fundamentação normativa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {servicos.map((servico, index) => (
              <motion.div
                key={servico.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="bg-gray-50 rounded-xl p-5 md:p-8 hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mb-4 md:mb-6">
                  <i className={`${servico.icon} text-xl md:text-2xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base md:text-xl text-[#0A1628] mb-3">
                  {servico.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {servico.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4 md:mb-6">
                Áreas de Atuação Pericial
              </h2>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
                Nossa equipe possui expertise técnica em diversas áreas da engenharia, garantindo avaliações precisas e laudos fundamentados tecnicamente.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {areas.map((area) => (
                  <div key={area} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{area}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://readdy.ai/api/search-image?query=engineer%20analyzing%20technical%20blueprints%20and%20documents%20on%20desk%20with%20laptop%20calculator%20and%20measuring%20instruments%20professional%20office%20environment%20conducting%20property%20evaluation%20and%20technical%20assessment&width=800&height=600&seq=per002&orientation=landscape"
                alt="Áreas de Atuação"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Diferenciais Técnicos
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Imparcialidade',
                description: 'Análises técnicas isentas e fundamentadas exclusivamente em critérios técnicos',
              },
              {
                icon: 'ri-book-open-line',
                title: 'Rigor Técnico',
                description: 'Laudos elaborados conforme normas ABNT e legislação vigente',
              },
              {
                icon: 'ri-time-line',
                title: 'Prazos Cumpridos',
                description: 'Compromisso com prazos judiciais e extrajudiciais estabelecidos',
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Documentação Completa',
                description: 'Laudos detalhados com fundamentação técnica e fotográfica',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mx-auto mb-4 md:mb-6">
                  <i className={`${item.icon} text-2xl md:text-3xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base md:text-xl text-[#0A1628] mb-2 md:mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Metodologia Pericial
            </h2>
            <p className="text-sm md:text-lg text-gray-600">
              Processo estruturado para garantir qualidade e precisão técnica
            </p>
          </motion.div>

          <div className="space-y-4 md:space-y-6">
            {[
              {
                numero: '01',
                titulo: 'Análise Preliminar',
                descricao: 'Estudo dos autos processuais ou documentação técnica, identificação dos quesitos e definição da metodologia de trabalho.',
              },
              {
                numero: '02',
                titulo: 'Vistoria Técnica',
                descricao: 'Inspeção in loco com registro fotográfico detalhado, medições, ensaios e coleta de informações técnicas necessárias.',
              },
              {
                numero: '03',
                titulo: 'Análise Técnica',
                descricao: 'Avaliação dos dados coletados, cálculos técnicos, consulta a normas e legislação aplicável, e fundamentação técnica.',
              },
              {
                numero: '04',
                titulo: 'Elaboração do Laudo',
                descricao: 'Redação do laudo pericial com conclusões fundamentadas, respostas aos quesitos e documentação técnica completa.',
              },
            ].map((etapa, index) => (
              <motion.div
                key={etapa.numero}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex items-start gap-4 md:gap-6 bg-white rounded-xl p-5 md:p-8 border border-gray-100"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-[#D4AF37] text-[#0A1628] rounded-xl font-display font-bold text-lg md:text-xl flex-shrink-0">
                  {etapa.numero}
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg md:text-2xl text-[#0A1628] mb-2">
                    {etapa.titulo}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                    {etapa.descricao}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-4 md:mb-6">
              Precisa de uma Perícia Técnica?
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-10">
              Fale com nossos especialistas e receba uma avaliação técnica precisa
            </p>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Tenho interesse em serviços de Avaliações e Perícias Técnicas. Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A1628] px-6 py-4 md:px-10 md:py-5 rounded-lg text-base md:text-lg font-bold hover:bg-[#C4A030] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default AvaliacoesPericiasPage;